import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as PackageUpIcon } from "assets/images/iconsV2/package-up.svg";
import { ReactComponent as PackageCheckIcon } from "assets/images/iconsV2/package-check.svg";
import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane.svg";
import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";
import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check.svg";
import { ReactComponent as XIcon } from "assets/images/iconsV2/x-close.svg";
import Badge from "components/v2/Badge/Badge";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { classNames } from "helpers/commonHelpers";

import { DetailsSheetSection, SheetField, isAffirmativeStatus } from "../DetailsSheetSection";
import {
  formatScheduleHours,
  normalizeScheduleStop,
  type NormalizedScheduleStop,
} from "../scheduleStopUtils";
import { SHIPMENT_DETAILS_TEST_IDS, shipmentDetailsTestId } from "../testIds";
import type { ShipmentDetailsSheetViewModel } from "../types";
import {
  getPartySyncKind,
  PartyContact,
  partyHasAddressDetails,
  toDetailsDeliveryInput,
  type ShippingParty,
} from "./SchedulePartyContact";

type ScheduleSectionProps = {
  schedule: ShipmentDetailsSheetViewModel["schedule"];
};

function StopTypeBadge({ isPickup }: { isPickup: boolean }) {
  const intl = useIntl();
  return (
    <Badge
      label={
        isPickup
          ? intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" })
          : intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" })
      }
      color={isPickup ? "indigo" : "success"}
      type="category-badge"
      variant="icon-leading"
      size="sm"
      icon={isPickup ? <PackageUpIcon /> : <PackageCheckIcon />}
    />
  );
}

function PortTypeBadge({ addressType, portLabel }: { addressType?: string; portLabel?: string }) {
  const intl = useIntl();
  const isAirport = addressType === "Airport";
  const isSeaport = addressType === "Port";
  if ((!isAirport && !isSeaport) || !portLabel) return null;

  return (
    <div className={classNames("rfv2-review__stop-address-type", "rfv2-review__stop-address-type--stack-on-mobile")}>
      <Badge
        label={
          isAirport
            ? intl.formatMessage({ id: "request-form.review.schedule.airport", defaultMessage: "Airport" })
            : intl.formatMessage({ id: "request-form.review.schedule.port", defaultMessage: "Port" })
        }
        color="gray"
        type="status-badge"
        variant="icon-leading"
        size="sm"
        icon={isAirport ? <PlaneIcon width={12} height={12} /> : <CargoShipIcon width={12} height={12} />}
      />
      <span className="rfv2-review__field-value">{portLabel}</span>
    </div>
  );
}

function StopAddressLines({
  stop,
  hideStreetWhenPortAddress,
}: {
  stop: NormalizedScheduleStop;
  hideStreetWhenPortAddress: boolean;
}) {
  const hideStreet = hideStreetWhenPortAddress && stop.isPortAddress;

  return (
    <div className="rfv2-review__stop-address">
      <PortTypeBadge addressType={stop.addressType} portLabel={stop.portLabel} />
      {stop.addressName && !hideStreet && <span className="rfv2-review__field-value">{stop.addressName}</span>}
      {stop.streetLine && !hideStreet && <span className="rfv2-review__field-value">{stop.streetLine}</span>}
      {stop.regionLine && (!hideStreet || stop.isPortAddress) && (
        <span className="rfv2-review__field-value">{stop.regionLine}</span>
      )}
      {(stop.unLocode || stop.locationReferenceNumber || stop.customAddressReference) && (
        <AddressLocodeAndCustomFields
          un_locode={stop.unLocode}
          location_reference_number={stop.locationReferenceNumber}
          custom_address_reference={stop.customAddressReference}
        />
      )}
    </div>
  );
}

function EndpointStop({
  stop,
  testId,
  hideStreetWhenPortAddress,
}: {
  stop: NormalizedScheduleStop;
  testId: string;
  hideStreetWhenPortAddress: boolean;
}) {
  const intl = useIntl();
  const hoursText = formatScheduleHours(stop.hoursStart, stop.hoursEnd);
  const hasContact = Boolean(stop.contactName || stop.contactPhone || stop.contactEmail);

  return (
    <div className="rfv2-review__stop-row" data-testid={testId}>
      <div className="rfv2-review__stop-location">
        <div className="rfv2-review__stop-top">
          <StopTypeBadge isPickup={stop.isPickup} />
          {stop.requestedTime && stop.requestedTime !== "-" && (
            <span className="rfv2-review__stop-date" data-testid={`${testId}-requested-time`}>
              {stop.requestedTime}
            </span>
          )}
        </div>

        <StopAddressLines stop={stop} hideStreetWhenPortAddress={hideStreetWhenPortAddress} />

        {(stop.instructions || hoursText) && (
          <div className="rfv2-review__stop-instructions">
            {stop.instructions && (
              <div className="rfv2-review__stop-meta" data-testid={`${testId}-instructions`}>
                {intl.formatMessage(
                  { id: "request-form.review.schedule.instructions", defaultMessage: "Instructions: {value}" },
                  { value: stop.instructions },
                )}
              </div>
            )}
            {hoursText && (
              <div className="rfv2-review__stop-meta" data-testid={`${testId}-hours`}>
                {intl.formatMessage(
                  { id: "request-form.review.schedule.hours", defaultMessage: "Hours: {start} - {end}" },
                  { start: hoursText.split(" - ")[0], end: hoursText.split(" - ")[1] },
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {hasContact && (
        <div className="rfv2-review__stop-contact" data-testid={`${testId}-contact`}>
          <span className="rfv2-review__field-label">
            {intl.formatMessage({ id: "request-form.review.schedule.contact", defaultMessage: "Contact" })}
          </span>
          {stop.contactName && <span className="rfv2-review__field-value">{stop.contactName}</span>}
          {stop.contactPhone && <span className="rfv2-review__stop-meta">{stop.contactPhone}</span>}
          {stop.contactEmail && <span className="rfv2-review__stop-meta">{stop.contactEmail}</span>}
        </div>
      )}
    </div>
  );
}

function isBareYesOrNo(value: string) {
  return /^(yes|no)$/i.test(value.trim());
}

function ClearanceRow({
  testId,
  label,
  value,
  instructionsTestId,
  instructions,
}: {
  testId: string;
  label: ReactNode;
  value?: string;
  instructionsTestId: string;
  instructions?: string;
}) {
  if (!value) return null;

  const positive = isAffirmativeStatus(value);
  const showStatusDetail = !isBareYesOrNo(value);
  const showComments = positive && Boolean(instructions);

  return (
    <div className="rfv2-review__req-item" data-testid={testId}>
      {positive ? (
        <CheckIcon className={classNames("rfv2-review__req-icon", "rfv2-review__req-icon--check")} />
      ) : (
        <XIcon className={classNames("rfv2-review__req-icon", "rfv2-review__req-icon--x")} />
      )}
      <div>
        <span className="rfv2-review__req-text">{label}</span>
        {showStatusDetail && (
          <>
            <br />
            <span className="rfv2-review__req-text">{value}</span>
          </>
        )}
        {showComments && (
          <>
            <br />
            <span className="rfv2-review__req-comment" data-testid={instructionsTestId}>
              Comments: {instructions}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export function ScheduleSection({ schedule }: ScheduleSectionProps) {
  const intl = useIntl();
  const isPortAddressDetailRenderingEnabled = useFeatureFlag(
    FEATURE_FLAGS.FF_WEB10382_PORT_ADDRESS_DETAIL_RENDERING,
  );

  const normalizedStops = schedule.stops.map((stop) => normalizeScheduleStop(stop, intl));
  const isMultistop = normalizedStops.length > 2;
  const firstPickup = normalizedStops.find((stop) => stop.isPickup) ?? normalizedStops[0];
  const lastDelivery =
    [...normalizedStops].reverse().find((stop) => !stop.isPickup) ?? normalizedStops[normalizedStops.length - 1];
  const showLastDelivery = Boolean(lastDelivery) && lastDelivery !== firstPickup;

  const consignee = schedule.consignee as ShippingParty | undefined;
  const notifyParty = schedule.notifyParty as ShippingParty | undefined;
  const lastDeliveryRaw = [...schedule.stops].reverse().find((stop) => {
    const stopType = String((stop as { stop_type?: unknown })?.stop_type ?? "").toLowerCase();
    return stopType !== "pickup" && stopType !== "pick up";
  });
  const delivery = toDetailsDeliveryInput(lastDeliveryRaw);
  const consigneeSyncContext = { role: "consignee" as const, delivery };
  const notifySyncContext = { role: "notify" as const, consignee, delivery };
  const hasClearance = Boolean(
    schedule.clearance.export || schedule.clearance.transit || schedule.clearance.import,
  );
  const hasConsignee =
    Boolean(consignee && getPartySyncKind(consignee, consigneeSyncContext)) ||
    Boolean(consignee?.contact_name || consignee?.contact_phone || consignee?.contact_email) ||
    partyHasAddressDetails(consignee);
  const hasNotifyParty =
    Boolean(notifyParty && getPartySyncKind(notifyParty, notifySyncContext)) ||
    Boolean(notifyParty?.contact_name || notifyParty?.contact_phone || notifyParty?.contact_email) ||
    partyHasAddressDetails(notifyParty);

  return (
    <DetailsSheetSection
      testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SECTION_SCHEDULE}
      title={<FormattedMessage id="quote-request-detail.schedule" defaultMessage="Schedule" />}
    >
      <div className="rfv2-review__section-content rfv2-review__section-content--schedule">
        {!isMultistop && firstPickup && (
          <div className="shipment-details-sheet__schedule-endpoints">
            <EndpointStop
              stop={firstPickup}
              testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_FIRST_PICKUP}
              hideStreetWhenPortAddress={isPortAddressDetailRenderingEnabled}
            />
            {showLastDelivery && lastDelivery && (
              <EndpointStop
                stop={lastDelivery}
                testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_LAST_DELIVERY}
                hideStreetWhenPortAddress={isPortAddressDetailRenderingEnabled}
              />
            )}
          </div>
        )}

        {isMultistop && (
          <div className="shipment-details-sheet__schedule-table-wrap">
            <table
              className="shipment-details-sheet__schedule-table"
              data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_TABLE}
            >
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="request-form.review.schedule.table.address" defaultMessage="Address" />
                </th>
                <th>
                  <FormattedMessage
                    id="request-form.review.schedule.table.requestedTime"
                    defaultMessage="Requested time"
                  />
                </th>
                <th>
                  <FormattedMessage
                    id="request-form.review.schedule.table.instructions"
                    defaultMessage="Instructions"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {normalizedStops.map((stop, index) => {
                const hideStreet = isPortAddressDetailRenderingEnabled && stop.isPortAddress;
                const hoursText = formatScheduleHours(stop.hoursStart, stop.hoursEnd);
                return (
                  <tr key={index}>
                    <td data-testid={shipmentDetailsTestId.scheduleStop(index, "address")}>
                      <div className="rfv2-review-schedule-table__address">
                        <div
                          className="rfv2-review-schedule-table__badge"
                          data-testid={shipmentDetailsTestId.scheduleStop(index, "type")}
                        >
                          <StopTypeBadge isPickup={stop.isPickup} />
                        </div>
                        <div className="rfv2-review-schedule-table__address-lines">
                          <PortTypeBadge addressType={stop.addressType} portLabel={stop.portLabel} />
                          {stop.addressName && !hideStreet && (
                            <span className="rfv2-review__field-value">{stop.addressName}</span>
                          )}
                          {stop.streetLine && !hideStreet && (
                            <span className="rfv2-review__field-value">{stop.streetLine}</span>
                          )}
                          {stop.regionLine && <span className="rfv2-review__field-value">{stop.regionLine}</span>}
                          {(stop.unLocode || stop.locationReferenceNumber || stop.customAddressReference) && (
                            <AddressLocodeAndCustomFields
                              un_locode={stop.unLocode}
                              location_reference_number={stop.locationReferenceNumber}
                              custom_address_reference={stop.customAddressReference}
                            />
                          )}
                        </div>
                        {hoursText && (
                          <span
                            className="rfv2-review-schedule-table__hours"
                            data-testid={shipmentDetailsTestId.scheduleStop(index, "hours")}
                          >
                            {intl.formatMessage(
                              { id: "request-form.review.schedule.hours", defaultMessage: "Hours: {start} - {end}" },
                              { start: hoursText.split(" - ")[0], end: hoursText.split(" - ")[1] },
                            )}
                          </span>
                        )}
                        {stop.contactSummary && (
                          <span
                            className="rfv2-review-schedule-table__contact"
                            data-testid={shipmentDetailsTestId.scheduleStop(index, "contact")}
                          >
                            {stop.contactSummary}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-testid={shipmentDetailsTestId.scheduleStop(index, "requested-time")}>
                      {stop.requestedTime}
                    </td>
                    <td data-testid={shipmentDetailsTestId.scheduleStop(index, "instructions")}>
                      <span className="rfv2-review__field-value">{stop.instructions || "-"}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {schedule.multistopInstructions && (
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_MULTISTOP_INSTRUCTIONS}
            label={
              <FormattedMessage
                id="new-request.schedule.multistop.multistopInstructions"
                defaultMessage="Multi-stop instructions"
              />
            }
            value={schedule.multistopInstructions}
          />
        )}

        {(hasConsignee || hasNotifyParty) && (
          <div
            className="shipment-details-sheet__schedule-parties"
            data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_PARTIES}
          >
            <PartyContact
              testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_CONSIGNEE}
              label={<FormattedMessage id="request-form.review.schedule.consignee" defaultMessage="Consignee" />}
              party={hasConsignee ? consignee : undefined}
              syncContext={consigneeSyncContext}
            />
            <PartyContact
              testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_NOTIFY_PARTY}
              label={<FormattedMessage id="request-form.review.schedule.notifyParty" defaultMessage="Notify party" />}
              party={hasNotifyParty ? notifyParty : undefined}
              syncContext={notifySyncContext}
            />
          </div>
        )}

        {hasClearance && (
          <div
            className="rfv2-review__service_field"
            data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_CLEARANCE}
          >
            <span className="rfv2-review__field-label">
              <FormattedMessage id="new-request.organisational-details.clearance" defaultMessage="Clearance" />
            </span>
            <div className="rfv2-review__req-columns">
              <div className="rfv2-review__req-column shipment-details-sheet__clearance-list">
                <ClearanceRow
                  testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_EXPORT_CLEARANCE}
                  label={
                    <FormattedMessage
                      id="new-request.service.exportClearanceNeeded"
                      defaultMessage="Export clearance needed?"
                    />
                  }
                  value={schedule.clearance.export}
                  instructionsTestId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_EXPORT_CLEARANCE_INSTRUCTIONS}
                  instructions={schedule.clearance.exportInstructions}
                />
                <ClearanceRow
                  testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_TRANSIT_CLEARANCE}
                  label={
                    <FormattedMessage
                      id="new-request.service.transitClearanceNeeded"
                      defaultMessage="Transit clearance needed?"
                    />
                  }
                  value={schedule.clearance.transit}
                  instructionsTestId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_TRANSIT_CLEARANCE_INSTRUCTIONS}
                  instructions={schedule.clearance.transitInstructions}
                />
                <ClearanceRow
                  testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_IMPORT_CLEARANCE}
                  label={
                    <FormattedMessage
                      id="new-request.service.importClearanceNeeded"
                      defaultMessage="Import clearance needed?"
                    />
                  }
                  value={schedule.clearance.import}
                  instructionsTestId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_IMPORT_CLEARANCE_INSTRUCTIONS}
                  instructions={schedule.clearance.importInstructions}
                />
              </div>
            </div>
          </div>
        )}

        {schedule.mileage && (
          <SheetField
            testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SCHEDULE_MILEAGE}
            label={<FormattedMessage id="quote-request-detail.mileage" defaultMessage="Mileage" />}
            value={schedule.mileage}
          />
        )}
      </div>
    </DetailsSheetSection>
  );
}
